/**
 * @module manifest/groupRegistryDefs/chartVisibility
 * @description 各配置组按图表类型的显隐规则（panelLayout 只列组 id，规则在此声明）
 */

import { ChartType, type TChartType } from '@dbl/core/chart';
import type { IGroupChartVisibility } from '../defineGroup';
import {
  and,
  hasMultiDimensions,
  horizontalBarLegendItemsVisibility,
  horizontalBarXAxisItemsVisibility,
  horizontalBarYAxisItemsVisibility,
  isAggregation,
  isChartSwitchVisible,
  notCrosstab,
  isToolTipSettingVisible,
  isXAxisSettingVisible,
  isYAxisSettingVisible,
  eventLineXAxisItemsVisibility,
  lineFamilyXAxisItemsVisibility,
  mixedChartXAxisItemsVisibility,
  showLineDisplayLimit,
  toolTipSettingItemsVisibility,
  xAxisSettingItemsVisibility,
  yAxisSettingItemsVisibility,
} from '../visibility/visibilityFactories';

const HIDE_SERIES_SORT_BY: IGroupChartVisibility['itemsVisibility'] = {
  SeriesSortBy: () => false,
};

const LEGEND_MULTI_DIM_SORT: IGroupChartVisibility['itemsVisibility'] = {
  SeriesSortBy: hasMultiDimensions,
};

export const DISPLAY_LIMIT_CHART_VISIBILITY: Partial<
Record<TChartType, IGroupChartVisibility>
> = {
  [ChartType.Line]: { visible: showLineDisplayLimit },
  [ChartType.PercentLine]: { visible: showLineDisplayLimit },
};

export const X_AXIS_SETTING_CHART_VISIBILITY: Partial<
Record<TChartType, IGroupChartVisibility>
> = {
  [ChartType.Line]: {
    visible: isXAxisSettingVisible,
    itemsVisibility: lineFamilyXAxisItemsVisibility,
  },
  [ChartType.PercentLine]: {
    visible: isXAxisSettingVisible,
    itemsVisibility: lineFamilyXAxisItemsVisibility,
  },
  [ChartType.EventLine]: {
    visible: isXAxisSettingVisible,
    itemsVisibility: eventLineXAxisItemsVisibility,
  },
  [ChartType.Bar]: {
    visible: isXAxisSettingVisible,
    itemsVisibility: xAxisSettingItemsVisibility,
  },
  [ChartType.StackBar]: {
    visible: isXAxisSettingVisible,
    itemsVisibility: xAxisSettingItemsVisibility,
  },
  [ChartType.PercentStackBar]: {
    visible: isXAxisSettingVisible,
    itemsVisibility: xAxisSettingItemsVisibility,
  },
  [ChartType.HorizontalBarChart]: {
    visible: isXAxisSettingVisible,
    itemsVisibility: horizontalBarXAxisItemsVisibility,
  },
  [ChartType.Pie]: {
    visible: isXAxisSettingVisible,
    itemsVisibility: xAxisSettingItemsVisibility,
  },
  [ChartType.MixedChart]: {
    visible: isXAxisSettingVisible,
    itemsVisibility: mixedChartXAxisItemsVisibility,
  },
};

export const Y_AXIS_SETTING_CHART_VISIBILITY: Partial<
Record<TChartType, IGroupChartVisibility>
> = {
  [ChartType.Line]: {
    visible: isYAxisSettingVisible,
    itemsVisibility: yAxisSettingItemsVisibility,
  },
  [ChartType.EventLine]: {
    visible: isYAxisSettingVisible,
    itemsVisibility: yAxisSettingItemsVisibility,
  },
  [ChartType.Bar]: {
    visible: isYAxisSettingVisible,
    itemsVisibility: yAxisSettingItemsVisibility,
  },
  [ChartType.StackBar]: {
    visible: isYAxisSettingVisible,
    itemsVisibility: yAxisSettingItemsVisibility,
  },
  [ChartType.PercentStackBar]: {
    visible: isYAxisSettingVisible,
    itemsVisibility: yAxisSettingItemsVisibility,
  },
  [ChartType.PercentLine]: {
    visible: isYAxisSettingVisible,
    itemsVisibility: yAxisSettingItemsVisibility,
  },
  [ChartType.HorizontalBarChart]: {
    visible: isYAxisSettingVisible,
    itemsVisibility: horizontalBarYAxisItemsVisibility,
  },
};

export const LEGEND_SETTING_CHART_VISIBILITY: Partial<
Record<TChartType, IGroupChartVisibility>
> = {
  [ChartType.Line]: { itemsVisibility: HIDE_SERIES_SORT_BY },
  [ChartType.EventLine]: { itemsVisibility: HIDE_SERIES_SORT_BY },
  [ChartType.Bar]: { itemsVisibility: LEGEND_MULTI_DIM_SORT },
  [ChartType.StackBar]: { itemsVisibility: LEGEND_MULTI_DIM_SORT },
  [ChartType.PercentStackBar]: { itemsVisibility: LEGEND_MULTI_DIM_SORT },
  [ChartType.PercentLine]: { itemsVisibility: HIDE_SERIES_SORT_BY },
  [ChartType.HorizontalBarChart]: {
    itemsVisibility: horizontalBarLegendItemsVisibility,
  },
};

export const CHART_SWITCH_CHART_VISIBILITY: Partial<
Record<TChartType, IGroupChartVisibility>
> = {
  [ChartType.Line]: { visible: isChartSwitchVisible },
  [ChartType.Bar]: { visible: isChartSwitchVisible },
  [ChartType.Table]: {
    visible: and(isChartSwitchVisible, isAggregation),
  },
};

export const TOOL_TIP_SETTING_CHART_VISIBILITY: Partial<
Record<TChartType, IGroupChartVisibility>
> = {
  [ChartType.StackBar]: {
    visible: isToolTipSettingVisible,
    itemsVisibility: toolTipSettingItemsVisibility,
  },
  [ChartType.PercentStackBar]: {
    visible: isToolTipSettingVisible,
    itemsVisibility: toolTipSettingItemsVisibility,
  },
  [ChartType.MixedChart]: {
    visible: isToolTipSettingVisible,
    itemsVisibility: toolTipSettingItemsVisibility,
  },
};

export const TABLE_FIELD_SETTING_CHART_VISIBILITY: Partial<
Record<TChartType, IGroupChartVisibility>
> = {
  [ChartType.Table]: { visible: isAggregation },
};

export const PIVOT_TABLE_CHART_VISIBILITY: Partial<
Record<TChartType, IGroupChartVisibility>
> = {
  [ChartType.Table]: { visible: isAggregation },
};

export const MERGE_SAME_CELLS_CHART_VISIBILITY: Partial<
Record<TChartType, IGroupChartVisibility>
> = {
  [ChartType.Table]: {
    visible: and(isAggregation, notCrosstab),
  },
};

export const PERCENTAGE_OF_COLUMN_TOTAL_CHART_VISIBILITY: Partial<
Record<TChartType, IGroupChartVisibility>
> = {
  [ChartType.Table]: { visible: isAggregation },
};

/**
 * 指定维度列总计占比（DimensionPercentage）：
 * 与「列总计占比」同源逻辑——只在「表格类型 + 聚合模式」下显示。
 * 明细模式（mode === 'detail'）下没有「列总计」概念，整组不渲染。
 */
export const DIMENSION_PERCENTAGE_CHART_VISIBILITY: Partial<
Record<TChartType, IGroupChartVisibility>
> = {
  [ChartType.Table]: { visible: isAggregation },
};
